import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PROBLEM, SHAPE, EXCLUDE, CODE, HONEST, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const glow = interpolate(frame, [40, 80], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="gdpr · data portability" color={COLORS.cyan} />
          <Big>
            One function exports{" "}
            <span style={{ color: COLORS.cyan, opacity: glow }}>everything</span>
            <br />I know about a user
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 30,
            }}
          >
            exportUserData(userId, tenantId)
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="why · gdpr article 20" color={COLORS.cyan} />
        <Big size={72}>
          The user owns their data
          <br />
          <span style={{ color: COLORS.dim }}>and can take it elsewhere</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.violet,
            marginTop: 34,
          }}
        >
          → one structured JSON object, on request
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — SHAPE: what the export gathers */
const cell: React.CSSProperties = {
  fontFamily: FONTS.mono,
  fontSize: 21,
  lineHeight: 1.85,
  color: COLORS.text,
};
const sub: React.CSSProperties = { color: COLORS.dim, fontSize: 17 };
export const Scene03Shape: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.shape.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="what it gathers · 5 buckets" color={COLORS.green} />
        <div style={{ display: "flex", gap: 22, alignItems: "stretch" }}>
          <Panel accent="cyan" title="user profile" width={300}>
            <div style={cell}>
              id · email
              <br />
              full_name · role
              <br />
              <span style={sub}>created / updated</span>
            </div>
          </Panel>
          <Panel accent="green" title="memberships" width={300}>
            <div style={cell}>
              tenant_id
              <br />
              tenant name
              <br />
              <span style={sub}>role · joined_at</span>
            </div>
          </Panel>
          <Panel accent="lime" title="chat sessions" width={300}>
            <div style={cell}>
              every session
              <br />
              + all messages
              <br />
              <span style={sub}>role · content · ts</span>
            </div>
          </Panel>
          <Panel accent="violet" title="knowledge sources" width={300}>
            <div style={cell}>
              id · name
              <br />
              provider_type
              <br />
              <span style={sub}>metadata only</span>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={SHAPE} accent="green" />
  </AbsoluteFill>
);

/* 04 — EXCLUDE: what it refuses to include */
export const Scene04Exclude: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [40, 70], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [190, 220], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.exclude.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="what it refuses · by design" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <div style={{ opacity: c1 }}>
            <Panel accent="red" title="✕ connection_settings" width={460}>
              <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
                Source <b style={{ color: COLORS.red }}>credentials</b> (the
                connection_settings JSONB) never leave the system.
              </div>
            </Panel>
          </div>
          <div style={{ opacity: c2 }}>
            <Panel accent="red" title="✕ document chunks" width={460}>
              <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.5, color: COLORS.text }}>
                Raw chunk <b style={{ color: COLORS.red }}>content</b> belongs to the
                knowledge base — not to the user.
              </div>
            </Panel>
          </div>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 22,
            color: COLORS.amber,
            marginTop: 34,
          }}
        >
          portability ≠ exfiltration
        </div>
      </AbsoluteFill>
      <Caption lines={EXCLUDE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 05 — CODE: exclusion is structural (select-only columns) */
const TS = `// src/lib/data-lifecycle/gdpr-export.ts
const { data: user } = await adminClient
  .from('users')
  .select('id, email, full_name, role, created_at, updated_at')
  .eq('id', userId).single();

// knowledge sources — METADATA columns only
const { data: sources } = await adminClient
  .from('knowledge_sources')
  .select('id, name, provider_type, created_at, is_deleted')
  .eq('tenant_id', tenantId);
//        ↑ connection_settings is never named → never read`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={TS}
        language="tsx"
        filename="gdpr-export.ts"
        highlightLines={[9, 10, 11]}
        revealFrames={260}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — HONEST: status / what's not wired yet */
const PIPE: PipeNode[] = [
  { label: "function", value: "exportUserData", sub: "done · auditable", accent: "green" },
  { label: "http route", value: "GET /export", sub: "not wired yet", accent: "amber" },
  { label: "download", value: "JSON file", sub: "next piece", accent: "red" },
];
export const Scene06Honest: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.honest.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="honest status · build-in-public" color={COLORS.amber} />
      <div style={{ width: 1480, marginBottom: 30 }}>
        <PipelineGraph nodes={PIPE} startFrame={8} stepFrames={24} nodeWidth={260} />
      </div>
      <Callout label="↳ what's real today" accent="green" width={760}>
        The export logic exists and is auditable. There is{" "}
        <b style={{ color: COLORS.text }}>no API endpoint or download button</b> calling it
        yet — that's the next piece.
      </Callout>
    </AbsoluteFill>
    <Caption lines={HONEST} accent="amber" />
  </AbsoluteFill>
);

/* 07 — OUTRO */
export const Scene07Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Portability is one function.
          <br />
          <span style={{ color: COLORS.cyan }}>
            The security is what it won't export.
          </span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · data lifecycle
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
